import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppUpdateProvider } from "@/features/appUpdate/AppUpdateProvider";
import { API_BASE } from "@/lib/apiClient";
import { ServerUnavailableOverlay } from "@/components/ServerUnavailableOverlay";
import { Layout } from "@/components/Layout";
import { RequireAuth } from "@/components/RequireAuth";
import { BrandingProvider } from "@/lib/branding/BrandingProvider";
import { ThemeProvider } from "@/theme/ThemeProvider";
const DashboardPage = lazy(() =>
  import("@/pages/dashboard/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
import { LoginPage } from "@/pages/auth/LoginPage";
import { NoAppAccessPage } from "@/pages/auth/NoAppAccessPage";
import { NotFoundPage } from "@/pages/NotFound";
import { GuestsPage } from "@/pages/guests/GuestsPage";
import { GuestDetailPage } from "@/pages/guests/GuestDetailPage";
import { PitchesPage } from "@/pages/pitches/PitchesPage";
import { BookingsPage } from "@/pages/bookings/BookingsPage";
import { ArrivalsPage } from "@/pages/arrivals/ArrivalsPage";
import { DeparturesPage } from "@/pages/departures/DeparturesPage";
import { OnSiteGuestsPage } from "@/pages/on-site/OnSiteGuestsPage";
import { PitchPlannerPage } from "@/pages/planner/PitchPlannerPage";
import { SitePlanPage } from "@/pages/site-plan/SitePlanPage";
import { StammdatenLayout } from "@/pages/stammdaten/StammdatenLayout";
import { StammdatenOverviewPage } from "@/pages/stammdaten/StammdatenOverviewPage";
import { CategoriesPage } from "@/pages/stammdaten/CategoriesPage";
import { OnlineBookingPage } from "@/pages/stammdaten/OnlineBookingPage";
import { ArticlesPage } from "@/pages/stammdaten/ArticlesPage";
import { ChargeRulesPage } from "@/pages/stammdaten/ChargeRulesPage";
import { SettingsPage } from "@/pages/stammdaten/SettingsPage";
import { GrundeinstellungenPage } from "@/pages/stammdaten/GrundeinstellungenPage";
import { GuestTabletIdlePage } from "@/pages/stammdaten/GuestTabletIdlePage";
import { InfoScreenConfigPage } from "@/pages/stammdaten/InfoScreenConfigPage";
import { TextTemplatesPage } from "@/pages/stammdaten/TextTemplatesPage";
import { SitePlanStammdatenPage } from "@/pages/stammdaten/SitePlanStammdatenPage";
import { SitePlanEditorPage } from "@/pages/stammdaten/SitePlanEditorPage";
import { ArticleCategoriesPage } from "@/pages/stammdaten/ArticleCategoriesPage";
import { DiscountsPage } from "@/pages/stammdaten/DiscountsPage";
import { SuppliersPage } from "@/pages/stammdaten/SuppliersPage";
import { AccountsPage } from "@/pages/stammdaten/AccountsPage";
import { ElectricityOverviewPage } from "@/pages/electricity/ElectricityOverviewPage";
import { SchnittstellenPage } from "@/pages/stammdaten/SchnittstellenPage";
import { WebsiteNewsPage } from "@/pages/stammdaten/WebsiteNewsPage";
import { WebsiteContentPage } from "@/pages/stammdaten/WebsiteContentPage";
import { AuditLogPage } from "@/pages/audit/AuditLogPage";
import { TicketsPage } from "@/pages/tickets/TicketsPage";
import { TicketDetailPage } from "@/pages/tickets/TicketDetailPage";
import { InvoicesPage } from "@/pages/invoices/InvoicesPage";
import { MailPage } from "@/pages/mail/MailPage";
import { MailCenterPage } from "@/pages/mail/MailCenterPage";
import { CashbookPage } from "@/pages/cashbook/CashbookPage";
import { DailyClosingPage } from "@/pages/daily-closing/DailyClosingPage";
import { ReportsPage } from "@/pages/reports/ReportsPage";
import { PriceSimulationPage } from "@/pages/reports/PriceSimulationPage";
import { ContractsPage } from "@/pages/contracts/ContractsPage";
import { VisitorsPage } from "@/pages/visitors/VisitorsPage";
import { QuickSalePage } from "@/pages/pos/QuickSalePage";
import { GuestTabletPage } from "@/pages/guest-tablet/GuestTabletPage";
import { GuestTabletDemoPage } from "@/pages/guest-tablet/GuestTabletDemoPage";
import { TouristPanelPage } from "@/pages/tourist-panel/TouristPanelPage";
import { GuestMobilePage } from "@/pages/guest-tablet/GuestMobilePage";
import { GuestTabletControlPage } from "@/pages/guest-tablet/GuestTabletControlPage";
import { InfoScreenPage } from "@/pages/info-screen/InfoScreenPage";

function StammdatenSchnittstellenIntegrationRedirect({
  integration,
  hash,
}: {
  integration: string;
  hash?: string;
}) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set("integration", integration);
  const search = params.toString();
  return (
    <Navigate
      to={`/stammdaten/schnittstellen${search ? `?${search}` : ""}${hash ?? ""}`}
      replace
    />
  );
}

export function App() {
  return (
    <ThemeProvider>
      <BrandingProvider>
      <ServerUnavailableOverlay apiBase={API_BASE} />
      <BrowserRouter>
        <AppUpdateProvider>
        <Routes>
          <Route path="login" element={<LoginPage />} />
          <Route path="kein-zugang" element={<NoAppAccessPage />} />
          <Route path="guest-tablet/demo/:workstationCode" element={<GuestTabletDemoPage />} />
          <Route path="guest-tablet/:workstationCode" element={<GuestTabletPage />} />
          <Route path="tourist-panel/:workstationCode" element={<TouristPanelPage />} />
          <Route path="guest/m/:token" element={<GuestMobilePage />} />
          <Route path="info-screen/:displayCode" element={<InfoScreenPage />} />
          <Route
            path="reception/guest-tablet"
            element={
              <RequireAuth>
                <GuestTabletControlPage />
              </RequireAuth>
            }
          />
          <Route element={<Layout />}>
            <Route
              index
              element={
                <RequireAuth>
                  <Suspense
                    fallback={
                      <p className="py-16 text-center text-sm text-label-secondary">
                        Dashboard wird geladen…
                      </p>
                    }
                  >
                    <DashboardPage />
                  </Suspense>
                </RequireAuth>
              }
            />
            <Route
              path="guests"
              element={
                <RequireAuth>
                  <GuestsPage />
                </RequireAuth>
              }
            />
            <Route
              path="guests/:guestId"
              element={
                <RequireAuth>
                  <GuestDetailPage />
                </RequireAuth>
              }
            />
            <Route
              path="contracts"
              element={
                <RequireAuth>
                  <ContractsPage />
                </RequireAuth>
              }
            />
            <Route
              path="besucher"
              element={
                <RequireAuth>
                  <VisitorsPage />
                </RequireAuth>
              }
            />
            <Route path="pitches" element={<Navigate to="/stammdaten/plaetze" replace />} />
            <Route
              path="stammdaten"
              element={
                <RequireAuth>
                  <StammdatenLayout />
                </RequireAuth>
              }
            >
              <Route index element={<StammdatenOverviewPage />} />
              <Route path="plaetze" element={<PitchesPage />} />
              <Route path="platzplan" element={<SitePlanStammdatenPage />} />
              <Route path="platzplan-editor" element={<SitePlanEditorPage />} />
              <Route path="kategorien" element={<CategoriesPage />} />
              <Route
                path="anreisearten"
                element={<Navigate to="/stammdaten/online-buchung" replace />}
              />
              <Route path="online-buchung" element={<OnlineBookingPage />} />
              <Route path="website" element={<WebsiteContentPage />} />
              <Route path="website-news" element={<WebsiteNewsPage />} />
              <Route path="artikel-kategorien" element={<ArticleCategoriesPage />} />
              <Route path="artikel" element={<ArticlesPage />} />
              <Route path="lieferanten" element={<SuppliersPage />} />
              <Route path="konten" element={<AccountsPage />} />
              <Route path="rabatte" element={<DiscountsPage />} />
              <Route path="zuordnung" element={<ChargeRulesPage />} />
              <Route path="texte" element={<TextTemplatesPage />} />
              <Route path="gaeste-tablet" element={<GuestTabletIdlePage />} />
              <Route path="infobildschirm" element={<InfoScreenConfigPage />} />
              <Route path="grundeinstellungen" element={<GrundeinstellungenPage />} />
              <Route path="einstellungen" element={<SettingsPage />} />
              <Route path="schnittstellen" element={<SchnittstellenPage />} />
              <Route
                path="tse"
                element={<Navigate to="/stammdaten/schnittstellen?integration=tse" replace />}
              />
              <Route
                path="tourist-import"
                element={<StammdatenSchnittstellenIntegrationRedirect integration="tourist-import" />}
              />
              <Route
                path="fernauslese-kanaele"
                element={
                  <StammdatenSchnittstellenIntegrationRedirect
                    integration="fernauslese"
                    hash="#fernauslese-kanaele"
                  />
                }
              />
            </Route>
            <Route
              path="bookings"
              element={
                <RequireAuth>
                  <BookingsPage />
                </RequireAuth>
              }
            />
            <Route
              path="mail"
              element={
                <RequireAuth>
                  <MailPage />
                </RequireAuth>
              }
            />
            <Route
              path="mail/rundmail"
              element={
                <RequireAuth>
                  <MailCenterPage />
                </RequireAuth>
              }
            />
            <Route
              path="invoices"
              element={
                <RequireAuth>
                  <InvoicesPage />
                </RequireAuth>
              }
            />
            <Route
              path="pos"
              element={
                <RequireAuth>
                  <QuickSalePage />
                </RequireAuth>
              }
            />
            <Route
              path="cashbook"
              element={
                <RequireAuth>
                  <CashbookPage />
                </RequireAuth>
              }
            />
            <Route
              path="daily-closing"
              element={
                <RequireAuth>
                  <DailyClosingPage />
                </RequireAuth>
              }
            />
            <Route
              path="reports"
              element={
                <RequireAuth>
                  <ReportsPage />
                </RequireAuth>
              }
            />
            <Route
              path="reports/preisfindung"
              element={
                <RequireAuth>
                  <PriceSimulationPage />
                </RequireAuth>
              }
            />
            <Route
              path="arrivals"
              element={
                <RequireAuth>
                  <ArrivalsPage />
                </RequireAuth>
              }
            />
            <Route
              path="departures"
              element={
                <RequireAuth>
                  <DeparturesPage />
                </RequireAuth>
              }
            />
            <Route
              path="gaeste-am-platz"
              element={
                <RequireAuth>
                  <OnSiteGuestsPage />
                </RequireAuth>
              }
            />
            <Route
              path="strom"
              element={
                <RequireAuth>
                  <ElectricityOverviewPage />
                </RequireAuth>
              }
            />
            <Route
              path="platzplan"
              element={
                <RequireAuth>
                  <SitePlanPage />
                </RequireAuth>
              }
            />
            <Route
              path="planner"
              element={
                <RequireAuth>
                  <PitchPlannerPage />
                </RequireAuth>
              }
            />
            <Route
              path="audit"
              element={
                <RequireAuth>
                  <AuditLogPage />
                </RequireAuth>
              }
            />
            <Route
              path="tickets"
              element={
                <RequireAuth>
                  <TicketsPage />
                </RequireAuth>
              }
            />
            <Route
              path="tickets/:ticketId"
              element={
                <RequireAuth>
                  <TicketDetailPage />
                </RequireAuth>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
        </AppUpdateProvider>
      </BrowserRouter>
      </BrandingProvider>
    </ThemeProvider>
  );
}
